/* global localStorage */
import React from 'react'
import PropTypes from 'prop-types'
import { Grid, GridToolbar } from '@progress/kendo-react-grid'
import Renderers from './BsrsGrid.Renderers'
import CustomGridHeaderCell from './BsrsGrid.HeaderCell'
import { MemoryData, ServerData } from './dataProviders'
import moment from 'moment'
import { ErrorViewer } from '@/components/errorViewer'
import find from 'lodash/find'
import { toUtcDate } from '@/utils/dateUtilities'
import { LookupCell } from '@/components/BsrsGrid/lookupCell'
import { filterCell } from '@/components/BsrsGrid/BsrsGrid.GridFilterCell'
import { ExcelExport } from '@progress/kendo-react-excel-export'
import Icon from '@/components/Icon'
import Resx from '@/components/Resx'
import { exportGrid, ExportType } from './BsrsGrid.Export'
import { getStore } from '@/store'
import { dialogTypes } from '@/dialogs'
import { actionCreators as dialogActions } from '@/store/Dialogs'
import { debounce } from 'lodash'
import { localized } from '@/decorators/localized'
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl'
import { getGridLocalization } from './BsrsGrid.Localization'

const PAGE_SIZE = 20
const IN_EDIT = 'inEdit'
const ORIGINAL_VALUE = '__originalValue__'

@localized()
class BsrsGrid extends React.Component {
  _grid

  constructor (props, context) {
    super(props, context)
    this.gridRef = React.createRef()

    let localDataState = {}
    if (props.persistenceId && localStorage.getItem('grid-options-' + props.persistenceId)) {
      localDataState = JSON.parse(localStorage.getItem('grid-options-' + props.persistenceId))
      if (localDataState.filter && localDataState.filter.filters && localDataState.filter.filters.length) {
        localDataState.filter.filters.forEach((filter, i) => {
          if (filter.operator === 'between') {
            localDataState.filter.filters[i].value[0] = moment(localDataState.filter.filters[i].value[0]).utc()
            localDataState.filter.filters[i].value[1] = moment(localDataState.filter.filters[i].value[1]).utc()
          }
        })
      }
    }

    this.state = {
      dataState: { skip: 0, take: props.pageSize || PAGE_SIZE, sort: props.defaultSort, ...localDataState },
      data: props.items || [],
      lastReloadedAt: props.lastReloadedAt,
      loading: false,
      minWidthModeActive: false,
      colWidthOnWideScreen: 'auto',
      colMinWidth: this.props.colMinWidth ? this.props.colMinWidth : 150
    }

    const exitOnEnter = props.exitEditingOnEnter !== undefined ? props.exitEditingOnEnter : true
    this.renderers = new Renderers(this.enterEdit.bind(this), this.exitEdit.bind(this), IN_EDIT, exitOnEnter)
    this.lastEditedRow = null
    this.clearFilter = this.clearFilter.bind(this)
    this.openPdfExportDialog = this.openPdfExportDialog.bind(this)
    loadMessages(getGridLocalization(props.localize), props.locale)
  }

  componentDidMount () {
    window.addEventListener('resize', this.onResizeWindowGrid, false)
    this.onResizeWindowGrid()
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.onResizeWindowGrid, false)
    if (this.props.persistenceId) {
      localStorage.setItem('grid-options-' + this.props.persistenceId, JSON.stringify(this.state.dataState))
    }
  }

  componentDidUpdate () {
    if (this.props.persistenceId) {
      localStorage.setItem('grid-options-' + this.props.persistenceId, JSON.stringify(this.state.dataState))
    }
    if (this.state.error && this.props.persistenceId && localStorage.getItem('grid-options-' + this.props.persistenceId)) {
      localStorage.removeItem('grid-options-' + this.props.persistenceId)
      this.resetDataState()
      this.refresh()
    }
  }

  resetDataState () {
    this.setState({ dataState: { skip: 0, take: this.props.pageSize || PAGE_SIZE, sort: this.props.defaultSort } })
  }

  getColumns = (children = this.props.children) => getColumnsArray(children)

  calcMinWidth (columns) {
    let widthCalc = 0
    let undefinedCols = 0
    columns.forEach(column => {
      const colWidth = column.props.width
      if (colWidth) {
        const colWidthNum = parseInt(colWidth, 10)
        widthCalc = widthCalc + colWidthNum
      } else {
        undefinedCols = undefinedCols + 1
      }
    })
    const totalWidth = (undefinedCols * this.state.colMinWidth) + widthCalc
    if (undefinedCols > 0) {
      const gridWrapperWidth = this.gridWrapperElement.getBoundingClientRect().width
      this.setState({ colWidthOnWideScreen: (gridWrapperWidth - widthCalc - 1.5) / undefinedCols })
    }
    return totalWidth
  }

  returnWidthOfCol = width => {
    if (this.state.minWidthModeActive) {
      if (width) {
        return width
      } else {
        return this.state.colMinWidth
      }
    } else {
      if (width) {
        return width
      } else {
        return this.state.colWidthOnWideScreen
      }
    }
  }

  onResizeWindowGrid = debounce(() => this.onResizeWindowGridFunc(), 300)

  onResizeWindowGridFunc = () => {
    if (this.gridWrapperElement) {
      const columns = this.getColumns()
      if (columns.length === 0) {
        this.setState({ minWidthModeActive: false })
        return
      }

      const gridWrapperWidth = this.gridWrapperElement.getBoundingClientRect().width
      if (this.calcMinWidth(columns) > gridWrapperWidth) {
        this.setState({ minWidthModeActive: true })
      } else {
        this.setState({ minWidthModeActive: false })
      }
    }
  }

  static getDerivedStateFromProps (props, state) {
    if (props.lastReloadedAt && moment(state.lastReloadedAt).isBefore(props.lastReloadedAt)) {
      return {
        lastReloadedAt: props.lastReloadedAt,
        dataState: {
          ...state.dataState,
          skip: 0 // Reset to page 1 when data reloads to avoid showing blank page when data are filtered
        }
      }
    }
    return null
  }

  onDataLoading = () => {
    this.setState({ loading: true })
  }

  onDataLoaded = (data) => {
    this.setState({ ...this.state, error: null, ...data, loading: false })
  }
  getFilteredData = () => {
    const { dataSource, items, dataState } = this.getDataComponentProps()

    return exportGrid({
      exportType: ExportType.DATA,
      dataSource,
      items,
      dataState,
      columns: this._grid.columns,
      excelExporter: this._exporter,
      useServerProcessing: this.useServerProcessing(),
      sheetHeader: this.props.sheetHeader,
      localizeProps: this.props.localizeProps,
      localize: this.props.localize
    })
  }

  onDataStateChange = (event) => {
    this.setState({ ...this.state, dataState: event.data, loading: true })
  }

  onItemChange = (event) => {
    let eventValue = event.value

    const column = find(event.target.columns, x => x.field === event.field)
    if (column && column.editor === 'date') {
      eventValue = toUtcDate(event.value)
    }

    event.dataItem[event.field] = eventValue
    this.lastEditedRow = { item: event.dataItem, value: eventValue, field: event.field }

    this.setState({ data: this.state.data })
  }

  enterEdit (dataItem, field) {
    if (typeof this.props.canEditField === 'function') {
      if (!this.props.canEditField(field, dataItem)) {
        return
      }
    }
    if (dataItem[IN_EDIT] && this.state.editField === field) {
      return
    }

    this.exitEdit()

    dataItem[IN_EDIT] = field
    dataItem[ORIGINAL_VALUE] = dataItem[field]
    this.setState({
      editField: field
    })
  }

  exitEdit () {
    this.onItemEdit()

    this.state.data.forEach((d) => {
      delete d[IN_EDIT]
    })
    this.setState({
      data: this.state.data,
      editField: undefined
    })
  }

  onItemEdit () {
    if (typeof this.props.onItemEdited === 'function' && this.lastEditedRow != null) {
      if (this.lastEditedRow.item[ORIGINAL_VALUE] !== this.lastEditedRow.value) {
        this.props.onItemEdited(this.lastEditedRow)
      }
    }
    this.lastEditedRow = null
  }

  useServerProcessing () {
    const { dataSource } = this.props
    return (typeof dataSource === 'function')
  }

  getDataComponent () {
    return this.useServerProcessing() ? ServerData : MemoryData
  }

  getDataComponentProps () {
    const { items, loading, children, simpleView } = this.props
    const { dataState, lastReloadedAt } = this.state
    const columns = this.getColumns(children)
    const columnTypes = getColumnTypes(columns)
    const dateTimeProperties = columns
      .filter(x => (x.props || {}).filter === 'date')
      .map(x => (x.props || {}).field)
    return this.useServerProcessing()
      ? {
        getGridWrapperElement: () => this.gridWrapperElement,
        dataState,
        columnTypes,
        onLoad: this.onDataLoaded,
        onLoading: this.onDataLoading,
        dataSource: this.props.dataSource,
        dateTimeProperties,
        lastReloadedAt,
        loading,
        getAllData: simpleView
      }
      : {
        dataState,
        columnTypes,
        onLoad: this.onDataLoaded,
        loading,
        getGridWrapperElement: () => this.gridWrapperElement,
        items: items || [],
        getAllData: simpleView
      }
  }

  refresh = () => {
    this.setState({ lastReloadedAt: new Date() })
  }

  clearFilter = () => {
    this.setState({ dataState: { ...this.state.dataState, filter: null } })
    if (this.props.persistenceId && localStorage.getItem('grid-options-' + this.props.persistenceId)) {
      localStorage.removeItem('grid-options-' + this.props.persistenceId)
    }
  }

  toggleFilter = () => {
    this.setState({ hideFilters: !this.state.hideFilters })
  }

  openPdfExportDialog = (exportType, dataSource, items, dataState) => {
    getStore().dispatch(dialogActions.openDialog({
      dialogType: dialogTypes.exportPdf,
      onAction: ({ fontSize, paperSize }) => exportGrid({
        exportType,
        dataSource,
        items,
        dataState,
        columns: this._grid.columns,
        excelExporter: this._exporter,
        useServerProcessing: this.useServerProcessing(),
        fontSize,
        paperSize,
        sheetHeader: this.props.sheetHeader,
        localizeProps: this.props.localizeProps,
        localize: this.props.localize
      })
    }))
  }

  render () {
    const GridData = this.getDataComponent()
    const gridDataProps = this.getDataComponentProps()
    const { style, children, editable, disableExcelExport, simpleView, localizeProps, localize, ...additionalProps } = this.props
    const extendedChildren = this.getColumns(children).map(col => {
      return React.cloneElement(col, { headerCell: CustomGridHeaderCell, width: this.returnWidthOfCol(col.props.width) })
    })
    const editableProps = editable ? { editField: IN_EDIT } : {}
    const { data, loading, error } = this.state
    let noData = false
    if (data && data.length === 0 && !loading && (!gridDataProps.dataState || !gridDataProps.dataState.filter || !gridDataProps.dataState.filter.filters)) {
      noData = true
    }
    const { dataSource, items, dataState } = gridDataProps

    return (
      <div className='grid-wrapper' ref={el => {
        this.gridWrapperElement = el
      }}>
        {!disableExcelExport &&
        <ExcelExport ref={(exporter) => {
          this._exporter = exporter
        }} />
        }

        <GridData {...gridDataProps} />
        {error ? <ErrorViewer error={error} onClick={this.refresh} />
          : <LocalizationProvider language={this.props.locale}>
            <Grid
              total={this.state.total}
              data={this.state.data}
              skip={this.state.dataState.skip}
              pageSize={this.state.dataState.take}
              filter={this.state.dataState.filter}
              filterCellRender={filterCell}
              sort={this.state.dataState.sort}
              resizable
              sortable
              reorderable
              filterable={!simpleView && !this.state.hideFilters && this.props.filterable !== false}
              onItemChange={this.onItemChange}
              cellRender={this.renderers.cellRender}
              rowRender={this.renderers.rowRender}
              onDataStateChange={this.onDataStateChange}
              ref={(grid) => {
                this._grid = grid
              }}
              pageable={simpleView || noData
                ? false
                : {
                  info: true,
                  type: 'input',
                  pageSizes: true,
                  previousNext: true
                }}
              {...editableProps}
              {...additionalProps}
            >
              {!simpleView &&
              <GridToolbar className='k-grid-toolbar--clear'>
                <div style={{ display: 'inline-flex', justifyContent: 'flex-start', width: '100%' }}>
                  {this.props.actions}
                </div>
                {!disableExcelExport &&
                <React.Fragment>
                  <button onClick={() => exportGrid({
                    exportType: ExportType.XLS,
                    dataSource,
                    items,
                    dataState,
                    columns: this._grid.columns,
                    excelExporter: this._exporter,
                    useServerProcessing: this.useServerProcessing(),
                    sheetHeader: this.props.sheetHeader,
                    localizeProps,
                    localize
                  })} className='btn btn-secondary btn-sm'>
                    <Icon name='spreadsheet' />
                    <span><Resx name='Common.ExportToExcel' /></span>
                  </button>
                  <button onClick={() => this.openPdfExportDialog(ExportType.PDF, dataSource, items, dataState)} className='btn btn-secondary btn-sm'>
                    <Icon name='bxs-file-pdf' />
                    <span><Resx name='Common.ExportToPdf' /></span>
                  </button>
                </React.Fragment>
                }
                {this.useServerProcessing() &&
                <button onClick={this.refresh} className='btn btn-secondary btn-sm'>
                  <Icon name='loader-alt' />
                  <span><Resx name='Common.RefreshGrid' /></span>
                </button>
                }
                {this.props.filterable !== false &&
                <React.Fragment>
                  <button onClick={this.clearFilter} className='btn btn-secondary btn-sm'>
                    <Icon name='filter' />
                    <span><Resx name='Common.ClearFilters' /></span>
                  </button>
                  <button onClick={this.toggleFilter} className='btn btn-secondary btn-sm'>
                    <Icon name={this.state.hideFilters ? 'bx-filter-alt' : 'bxs-filter-alt'} />
                    <span><Resx name='Common.ToggleFilters' /></span>
                  </button>
                </React.Fragment>}
              </GridToolbar>
              }
              {extendedChildren}
            </Grid>
          </LocalizationProvider>
        }
      </div>
    )
  }
}

function getColumnsArray (children) {
  return React.Children.toArray(children).filter(React.isValidElement)
}

function getColumnTypes (columns) {
  const types = {}
  columns.forEach(x => {
    if (x.props && x.props.field) {
      types[x.props.field] = getColumnType(x)
    }
  })
  return types
}

function getColumnType (column) {
  if (column.props.cell === LookupCell) {
    return 'lookup'
  }
  return 'simple'
}

BsrsGrid.propTypes = {
  dataSource: PropTypes.func,
  canEditField: PropTypes.func,
  disableExcelExport: PropTypes.bool,
  simpleView: PropTypes.bool,
  persistenceId: PropTypes.string,
  sheetHeader: PropTypes.string
}

export default BsrsGrid
export {
  IN_EDIT
}



// WEBPACK FOOTER //
// ./src/components/BsrsGrid/BsrsGrid.js